import type { GrayImage } from "./types";

/**
 * Compute a global threshold for a grayscale image using Otsu's method, which
 * picks the value that maximises the between-class variance of the two pixel
 * populations (foreground / background).
 *
 * @returns A threshold in the range 0..255. Pixels strictly greater than the
 *          threshold are considered the "bright" class.
 */
export function otsuThreshold(image: GrayImage): number {
  const histogram = new Array<number>(256).fill(0);
  const { data } = image;
  for (let i = 0; i < data.length; i++) {
    histogram[data[i]]++;
  }

  const total = data.length;
  if (total === 0) return 127;

  let sum = 0;
  for (let t = 0; t < 256; t++) sum += t * histogram[t];

  let sumBackground = 0;
  let weightBackground = 0;
  let maxVariance = -1;
  let threshold = 0;

  for (let t = 0; t < 256; t++) {
    weightBackground += histogram[t];
    if (weightBackground === 0) continue;

    const weightForeground = total - weightBackground;
    if (weightForeground === 0) break;

    sumBackground += t * histogram[t];
    const meanBackground = sumBackground / weightBackground;
    const meanForeground = (sum - sumBackground) / weightForeground;

    const variance =
      weightBackground *
      weightForeground *
      (meanBackground - meanForeground) ** 2;

    if (variance > maxVariance) {
      maxVariance = variance;
      threshold = t;
    }
  }

  return threshold;
}
